import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { APIResponseWrapper } from '../models/common/api-response-wrapper';
import { environment } from 'src/environments/environment';
import {
  ContainerYardAppointment,
  ContainerYardArea,
  ContainerYardChargeAccrual,
  ContainerYardChargeRule,
  ContainerYardEir,
  ContainerYardGateInRequest,
  ContainerYardGateOutRequest,
  ContainerYardMove,
  ContainerYardMoveRequest,
  ContainerYardOccupancyOverview,
  ContainerYardSlot,
  ContainerYardVisit
} from './models/container-terminal-yard.model';

@Injectable({
  providedIn: 'root'
})
export class ContainerTerminalYardService {
  private readonly baseUrl = `${environment.restApiUrl}warehousing/container-yards`;

  constructor(private http: HttpClient) {}

  gateIn(payload: ContainerYardGateInRequest): Observable<ContainerYardVisit> {
    return this.unwrap(this.http.post<APIResponseWrapper<ContainerYardVisit>>(`${this.baseUrl}/visits/gate-in`, payload));
  }

  move(visitId: number, payload: ContainerYardMoveRequest): Observable<ContainerYardMove> {
    return this.unwrap(this.http.post<APIResponseWrapper<ContainerYardMove>>(`${this.baseUrl}/visits/${visitId}/moves`, payload));
  }

  gateOut(visitId: number, payload: ContainerYardGateOutRequest): Observable<ContainerYardVisit> {
    return this.unwrap(this.http.post<APIResponseWrapper<ContainerYardVisit>>(`${this.baseUrl}/visits/${visitId}/gate-out`, payload));
  }

  findVisits(params: HttpParams): Observable<ContainerYardVisit[]> {
    return this.unwrap(this.http.get<APIResponseWrapper<ContainerYardVisit[]>>(`${this.baseUrl}/visits`, { params }));
  }

  findInventory(params: HttpParams): Observable<ContainerYardVisit[]> {
    return this.unwrap(this.http.get<APIResponseWrapper<ContainerYardVisit[]>>(`${this.baseUrl}/inventory`, { params }));
  }

  occupancy(params: HttpParams): Observable<ContainerYardOccupancyOverview> {
    return this.unwrap(this.http.get<APIResponseWrapper<ContainerYardOccupancyOverview>>(`${this.baseUrl}/occupancy`, { params }));
  }

  findMoves(visitId: number): Observable<ContainerYardMove[]> {
    return this.unwrap(this.http.get<APIResponseWrapper<ContainerYardMove[]>>(`${this.baseUrl}/visits/${visitId}/moves`));
  }

  findAreas(params: HttpParams): Observable<ContainerYardArea[]> {
    return this.unwrap(this.http.get<APIResponseWrapper<ContainerYardArea[]>>(`${this.baseUrl}/areas`, { params }));
  }

  createArea(companyId: number, payload: ContainerYardArea): Observable<ContainerYardArea> {
    return this.unwrap(this.http.post<APIResponseWrapper<ContainerYardArea>>(`${this.baseUrl}/areas`, payload, {
      params: new HttpParams().set('companyId', String(companyId))
    }));
  }

  updateArea(companyId: number, areaId: number, payload: ContainerYardArea): Observable<ContainerYardArea> {
    return this.unwrap(this.http.put<APIResponseWrapper<ContainerYardArea>>(`${this.baseUrl}/areas/${areaId}`, payload, {
      params: new HttpParams().set('companyId', String(companyId))
    }));
  }

  findSlots(params: HttpParams): Observable<ContainerYardSlot[]> {
    return this.unwrap(this.http.get<APIResponseWrapper<ContainerYardSlot[]>>(`${this.baseUrl}/slots`, { params }));
  }

  createSlot(companyId: number, payload: ContainerYardSlot): Observable<ContainerYardSlot> {
    return this.unwrap(this.http.post<APIResponseWrapper<ContainerYardSlot>>(`${this.baseUrl}/slots`, payload, {
      params: new HttpParams().set('companyId', String(companyId))
    }));
  }

  updateSlot(companyId: number, slotId: number, payload: ContainerYardSlot): Observable<ContainerYardSlot> {
    return this.unwrap(this.http.put<APIResponseWrapper<ContainerYardSlot>>(`${this.baseUrl}/slots/${slotId}`, payload, {
      params: new HttpParams().set('companyId', String(companyId))
    }));
  }

  findAppointments(companyId: number, extraParams?: HttpParams): Observable<ContainerYardAppointment[]> {
    const params = (extraParams || new HttpParams()).set('companyId', String(companyId));
    return this.unwrap(this.http.get<APIResponseWrapper<ContainerYardAppointment[]>>(`${this.baseUrl}/appointments`, { params }));
  }

  saveAppointment(companyId: number, payload: ContainerYardAppointment): Observable<ContainerYardAppointment> {
    return this.unwrap(this.http.post<APIResponseWrapper<ContainerYardAppointment>>(`${this.baseUrl}/appointments`, payload, {
      params: new HttpParams().set('companyId', String(companyId))
    }));
  }

  findCharges(visitId: number): Observable<ContainerYardChargeAccrual[]> {
    return this.unwrap(this.http.get<APIResponseWrapper<ContainerYardChargeAccrual[]>>(`${this.baseUrl}/visits/${visitId}/charges`));
  }

  recalculateCharges(companyId: number, visitId: number): Observable<ContainerYardChargeAccrual[]> {
    return this.unwrap(this.http.post<APIResponseWrapper<ContainerYardChargeAccrual[]>>(`${this.baseUrl}/visits/${visitId}/charges/recalculate`, null, {
      params: new HttpParams().set('companyId', String(companyId))
    }));
  }

  findChargeRules(companyId: number): Observable<ContainerYardChargeRule[]> {
    return this.unwrap(this.http.get<APIResponseWrapper<ContainerYardChargeRule[]>>(`${this.baseUrl}/charge-rules`, {
      params: new HttpParams().set('companyId', String(companyId))
    }));
  }

  createChargeRule(companyId: number, payload: ContainerYardChargeRule): Observable<ContainerYardChargeRule> {
    return this.unwrap(this.http.post<APIResponseWrapper<ContainerYardChargeRule>>(`${this.baseUrl}/charge-rules`, payload, {
      params: new HttpParams().set('companyId', String(companyId))
    }));
  }

  updateChargeRule(companyId: number, id: number, payload: ContainerYardChargeRule): Observable<ContainerYardChargeRule> {
    return this.unwrap(this.http.put<APIResponseWrapper<ContainerYardChargeRule>>(`${this.baseUrl}/charge-rules/${id}`, payload, {
      params: new HttpParams().set('companyId', String(companyId))
    }));
  }

  deleteChargeRule(companyId: number, id: number): Observable<void> {
    return this.unwrap(this.http.delete<APIResponseWrapper<void>>(`${this.baseUrl}/charge-rules/${id}`, {
      params: new HttpParams().set('companyId', String(companyId))
    }));
  }

  findEirs(visitId: number): Observable<ContainerYardEir[]> {
    return this.unwrap(this.http.get<APIResponseWrapper<ContainerYardEir[]>>(`${this.baseUrl}/visits/${visitId}/eirs`));
  }

  eirPdfUrl(companyId: number, eirId: number): string {
    return `${this.baseUrl}/eirs/${eirId}/pdf?companyId=${encodeURIComponent(String(companyId))}`;
  }

  private unwrap<T>(source: Observable<APIResponseWrapper<T>>): Observable<T> {
    return source.pipe(map(response => response.data));
  }
}
